import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { Icon } from '../../shared/Icon';
import { DateField } from './DateField';
import { GiftDetailDialog } from './GiftDetailDialog';
import { JiraHtml } from './JiraHtml';
import { JiraIssueLink } from './JiraIssueLink';
import { PlannedGiftTile } from './PlannedGiftTile';
import {
    useWrappingGameElements,
    WrappingElementPicker,
} from './WrappingElementPicker';

import { GameElementDto } from '../../hooks/gameElements';
import { useGiftApi } from '../../hooks/useGiftApi';
import {
    JIRA_QUERY_KEYS,
    JiraIssueSummary,
    JiraSubtask,
    useCreateJiraSubtask,
} from '../../hooks/useJiraApi';

import { statusChipColor } from '../utils/status';

import { canScheduleSubtask, findGiftForSubtask } from '../domain/giftPlan';

export interface PlannedGiftCardProps {
    issue: JiraIssueSummary;
}

export const PlannedGiftCard = ({ issue }: PlannedGiftCardProps) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [subtaskSummary, setSubtaskSummary] = useState('');
    const [subtaskDescription, setSubtaskDescription] = useState('');
    const [wrappingElement, setWrappingElement] =
        useState<GameElementDto | null>(null);
    const [startTime, setStartTime] = useState<null | Date>(null);
    const [endTime, setEndTime] = useState<null | Date>(null);
    const [isBox, setIsBox] = useState(false);
    const createSubtask = useCreateJiraSubtask();
    const queryClient = useQueryClient();
    const { options: gameElementOptions } = useWrappingGameElements();

    const resetForm = () => {
        setSubtaskSummary('');
        setSubtaskDescription('');
        setWrappingElement(null);
        setStartTime(null);
        setEndTime(null);
        setIsBox(false);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    const handleCreate = () => {
        if (!subtaskSummary.trim()) return;
        createSubtask.mutate(
            {
                params: { path: { parentKey: issue.key } },
                body: {
                    summary: subtaskSummary.trim(),
                    description: subtaskDescription.trim() || undefined,
                    wrappingGameElementId: wrappingElement?.id || undefined,
                    startTime: startTime?.toISOString() || undefined,
                    endTime: endTime?.toISOString() || undefined,
                    giftBox: isBox || undefined,
                },
            },
            {
                onSuccess: () => {
                    closeDialog();
                    queryClient.invalidateQueries({
                        queryKey: JIRA_QUERY_KEYS.searchAll,
                    });
                },
            },
        );
    };

    const subtasks = issue.subtasks ?? [];

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
            }}
        >
            <CardContent
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {issue.summary}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                        >
                            <Chip
                                label={issue.status}
                                size="small"
                                variant="outlined"
                                color={statusChipColor(issue.statusCategory)}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {subtasks.length} gift
                                {subtasks.length === 1 ? '' : 's'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <JiraIssueLink url={issue.url} />
                </Stack>

                {issue.description && (
                    <JiraHtml
                        html={issue.description}
                        sx={{ color: 'text.secondary' }}
                    />
                )}

                {subtasks.length > 0 && (
                    <>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            {subtasks.map((st) => (
                                <PlannedGiftDetailTile
                                    key={st.key}
                                    subtask={st}
                                />
                            ))}
                        </Box>
                    </>
                )}
            </CardContent>

            {issue.statusCategory !== 'done' && (
                <Box sx={{ px: 2, pb: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Icon name="add" />}
                        onClick={() => setDialogOpen(true)}
                    >
                        Add gift
                    </Button>
                </Box>
            )}

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add gift to {issue.key}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            required
                            autoFocus
                            fullWidth
                            label="Summary"
                            value={subtaskSummary}
                            onChange={(e) => setSubtaskSummary(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={subtaskDescription}
                            onChange={(e) =>
                                setSubtaskDescription(e.target.value)
                            }
                            multiline
                            rows={3}
                        />
                        <WrappingElementPicker
                            value={wrappingElement}
                            onChange={setWrappingElement}
                            options={gameElementOptions}
                            label="Wrapping Game Element"
                        />
                        <Stack direction="row" spacing={2}>
                            <DateField
                                label="Start Date"
                                value={startTime}
                                onChange={setStartTime}
                            />
                            <DateField
                                label="End Date"
                                value={endTime}
                                onChange={setEndTime}
                            />
                        </Stack>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isBox}
                                    onChange={(e) => setIsBox(e.target.checked)}
                                />
                            }
                            label="Gift Box"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={handleCreate}
                        variant="contained"
                        disabled={
                            !subtaskSummary.trim() || createSubtask.isPending
                        }
                    >
                        {createSubtask.isPending ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

const PlannedGiftDetailTile = ({ subtask }: { subtask: JiraSubtask }) => {
    const [open, setOpen] = useState(false);

    const giftApi = useGiftApi();
    const giftsQuery = giftApi.useQuery('get', '/gift/api/v2/admin/gifts');
    const linkedGift = findGiftForSubtask(giftsQuery.data?.gifts, subtask);
    const scheduled = !!linkedGift;
    const canSchedule = canScheduleSubtask(subtask);

    return (
        <>
            <GiftDetailDialog
                subtask={subtask}
                gift={linkedGift}
                open={open}
                onClose={() => setOpen(false)}
                initialTab="planned"
                canSchedule={canSchedule}
            />
            <PlannedGiftTile
                subtask={subtask}
                status={
                    scheduled
                        ? 'scheduled'
                        : canSchedule
                          ? 'ready'
                          : 'incomplete'
                }
                onSelect={() => setOpen(true)}
            />
        </>
    );
};
